import React, { useEffect, useMemo, useState } from "react";
import { ChartLineUp } from "@phosphor-icons/react";
import { fetchAcresHistory } from "../lib/api";

const fmtAcres = (a) =>
  a >= 1000 ? `${(a / 1000).toFixed(1).replace(/\.0$/, "")}k` : a >= 100 ? Math.round(a).toString() : a.toFixed(1).replace(/\.0$/, "");

const fmtAcresFull = (a) =>
  a >= 100 ? Math.round(a).toLocaleString() : a.toFixed(1).replace(/\.0$/, "");

const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const GrowthChart = ({ points }) => {
  const [hover, setHover] = useState(null);
  const w = 280, h = 150;
  const padL = 38, padR = 12, padT = 14, padB = 24;
  const ts = points.map((p) => new Date(p.at).getTime());
  const vs = points.map((p) => p.acres);
  const tMin = Math.min(...ts), tMax = Math.max(...ts);
  let vMin = Math.min(...vs), vMax = Math.max(...vs);
  if (vMax === vMin) { vMax += 1; vMin = Math.max(0, vMin - 1); }
  const x = (t) => padL + ((t - tMin) / (tMax - tMin || 1)) * (w - padL - padR);
  const y = (v) => h - padB - ((v - vMin) / (vMax - vMin)) * (h - padT - padB);
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${x(new Date(p.at).getTime()).toFixed(1)},${y(p.acres).toFixed(1)}`)
    .join(" ");
  const yTicks = [vMin, (vMin + vMax) / 2, vMax];
  const last = points[points.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="size-growth-chart">
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={padL} x2={w - padR} y1={y(v)} y2={y(v)} stroke="#2A332E" strokeWidth="1" strokeDasharray={i === 0 ? "" : "2 4"} />
          <text x={padL - 5} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#FFFFFF" fontFamily="monospace">
            {fmtAcres(v)}
          </text>
        </g>
      ))}
      <text x={padL} y={h - 8} textAnchor="start" fontSize="9" fill="#FFFFFF" fontFamily="monospace">
        {fmtDay(points[0].at)}
      </text>
      <text x={w - padR} y={h - 8} textAnchor="end" fontSize="9" fill="#FFFFFF" fontFamily="monospace">
        {fmtDay(last.at)}
      </text>
      <path
        d={`${path} L${x(new Date(last.at).getTime())},${h - padB} L${padL},${h - padB} Z`}
        fill="#F2542D"
        opacity="0.1"
      />
      <path d={path} fill="none" stroke="#F2542D" strokeWidth="2" strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={x(new Date(p.at).getTime())} cy={y(p.acres)} r={hover === i ? 4 : 2.5} fill="#F2542D" stroke="#0F1311" strokeWidth="1" />
      ))}
      {points.map((p, i) => (
        <circle
          key={`hit-${i}`}
          cx={x(new Date(p.at).getTime())}
          cy={y(p.acres)}
          r="10"
          fill="transparent"
          style={{ cursor: "pointer" }}
          data-testid={`growth-point-${i}`}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
        />
      ))}
      {hover != null && (() => {
        const p = points[hover];
        const px = x(new Date(p.at).getTime());
        const py = y(p.acres);
        const label = `${fmtAcresFull(p.acres)} ac`;
        const sub = new Date(p.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
        const bw = Math.max(label.length, sub.length) * 7 + 16;
        const bx = Math.min(Math.max(px - bw / 2, padL), w - padR - bw);
        const above = py - 44 > padT;
        const by = above ? py - 44 : py + 10;
        return (
          <g pointerEvents="none" data-testid="growth-tooltip">
            <rect x={bx} y={by} width={bw} height={34} rx="3" fill="#1A211D" stroke="#3A453F" strokeWidth="1" />
            <text x={bx + bw / 2} y={by + 14} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#FFFFFF" fontFamily="monospace">
              {label}
            </text>
            <text x={bx + bw / 2} y={by + 27} textAnchor="middle" fontSize="10" fill="#FFFFFF" fontFamily="monospace">
              {sub}
            </text>
          </g>
        );
      })()}
    </svg>
  );
};

export const SizeHistoryBox = ({ incidentId, nifcId, perimId }) => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    fetchAcresHistory(incidentId, nifcId || perimId, 30)
      .then(setRows)
      .catch(() => setRows([]));
  }, [incidentId, nifcId, perimId]);

  const points = useMemo(() => {
    if (!rows?.length) return [];
    const sorted = [...rows].sort((a, b) => a.at.localeCompare(b.at));
    const counts = {};
    sorted.forEach((r) => { counts[r.source] = (counts[r.source] || 0) + 1; });
    const best = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
    const series = sorted.filter((r) => r.source === best);
    const out = [];
    for (const r of series) {
      if (out.length && Math.abs(out[out.length - 1].acres - r.acres) < 0.1) continue;
      out.push(r);
    }
    if (out.length === 1 && series.length > 1) out.push(series[series.length - 1]);
    return out;
  }, [rows]);

  if (rows === null || points.length < 2) return null;

  const first = points[0];
  const last = points[points.length - 1];
  const delta = last.acres - first.acres;

  return (
    <div className="border border-tac-border bg-tac-surface/40 p-4" data-testid="size-history-box">
      <div className="flex items-center gap-2 mb-1">
        <ChartLineUp size={14} weight="duotone" className="text-tac-primary" />
        <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
          Fire Growth
        </span>
      </div>
      <div className="text-[13px] text-tac-dim mb-2" data-testid="size-growth-summary">
        {fmtAcresFull(first.acres)} → <span className="font-bold text-tac-text">{fmtAcresFull(last.acres)} acres</span>
        {Math.abs(delta) >= 0.5 && (
          <span style={{ color: delta > 0 ? "#E63946" : "#6BBF59" }}>
            {" "}({delta > 0 ? "+" : "−"}{fmtAcresFull(Math.abs(delta))})
          </span>
        )}
      </div>
      <GrowthChart points={points} />
    </div>
  );
};

export default SizeHistoryBox;
